import { useMemo, useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { MEASUREMENT_TYPES } from "../../../../../../config/enums/MeasurementTypes";
import { errorView, successMessage } from "../../../../../../helpers/ErrorHandler";
import { removeNewDrawingItemStatus, saveNewDrawingItem } from "../../../../../../utils/cacheStorage";
import { PACKAGING_SAVE_STATUS } from "../packagingLevel.config";
import {
  PACKAGING_GATE,
  PackagingDeleteModalProps,
  PackagingLevelProps
} from "../packagingLevel.types";
import { validatePackageData } from "../packagingValidation.utils";
import { getPackagingLevelMeta } from "../packagingLevel.config";
import {
  MATERIAL_DRAFT_CLEARED_VALUES,
  MATERIAL_DRAFT_FIELD_NAMES
} from "../utils/materialDraftDefaults";

interface UsePackagingLevelMaterialsOptions extends PackagingLevelProps {
  form: UseFormReturn<any>;
  showMaterialsSection: boolean;
  openAccordion: () => void;
}

export function usePackagingLevelMaterials({
  form,
  showMaterialsSection,
  openAccordion,
  gate,
  gateId,
  mutations,
  packagingData,
  refetchData,
  packagingType,
  packagingId,
  savePackingNodeData,
  packagingTypeData,
  unitSizes,
  palletLength,
  palletWidth,
  lockStatus = false,
  onPackagingSave = () => {}
}: UsePackagingLevelMaterialsOptions) {
  const [enableSizeEditStatus, setEnableSizeEditStatus] = useState(false);
  const [defaultType, setDefaultType] = useState<number>(0);
  const [materialWeightUnit, setMaterialWeightUnit] = useState<MEASUREMENT_TYPES>(
    MEASUREMENT_TYPES.WEIGHT_KG
  );
  const [visibleDeleteModal, setVisibleDeleteModal] = useState(false);
  const [deleteData, setDeleteData] = useState<any>({});
  const [packageId, setPackageId] = useState<string | null>(null);

  const levelMeta = getPackagingLevelMeta(packagingType);
  const fieldName = levelMeta?.name ?? "baseUnit";
  const { isLoading, create, update, delete: remove } = mutations;

  const {
    formState: { errors: errorsUnit },
    handleSubmit,
    setValue,
    setError,
    watch,
    clearErrors
  } = form;

  const packageNameValue = watch("packageName");
  const materialWeightValue = watch("materialWeight");
  const packageMaterialValue = watch("packageMaterial");

  const canSavePackagingMeta =
    !!packageNameValue &&
    !!packageMaterialValue &&
    !!materialWeightValue &&
    !errorsUnit?.packageName &&
    !errorsUnit?.packageMaterial &&
    !errorsUnit?.materialWeight &&
    !errorsUnit?.materialCost &&
    !lockStatus &&
    !isLoading;

  const clearMaterialDraft = () => {
    MATERIAL_DRAFT_FIELD_NAMES.forEach(field => {
      setValue(field, MATERIAL_DRAFT_CLEARED_VALUES[field]);
    });
    setDefaultType(0);
    setMaterialWeightUnit(MEASUREMENT_TYPES.WEIGHT_KG);
    setEnableSizeEditStatus(false);
    setPackageId(null);
    clearErrors(["packageName", "materialWeight", "materialCost", "packageMaterial"]);
  };

  const setUnitSizeUpdate = (data: any) => {
    setValue("packageName", data.packageName);
    setValue("materialWeight", data.materialWeight);
    setValue("materialCost", data.materialCost);
    setValue("packageMaterial", data?.packageMaterial ? String(data.packageMaterial) : "");
    setValue("materialWeightUnit", String(Number(data?.weightUnit) || MEASUREMENT_TYPES.WEIGHT_KG));
    setDefaultType(data?.packageMaterial || 0);
    setMaterialWeightUnit(Number(data?.weightUnit) || MEASUREMENT_TYPES.WEIGHT_KG);
  };

  const commentResetValue = (statusMessage: string) => {
    // Clear material draft only — do not reset() the shared form (wipes unit size fields).
    clearMaterialDraft();
    successMessage(statusMessage);
  };

  const deletePackageData = (data: any) => {
    const deletePackagingData =
      gate === PACKAGING_GATE.FARM
        ? { packagingType, farmGateId: gateId, packagingId, subPackagingId: data._id }
        : { packagingType, processGateId: gateId, packagingId, subPackagingId: data._id };

    const onSuccess = () => {
      onPackagingSave(data.packagingId);
      refetchData();
      successMessage("Package deleted successfully");
    };

    remove.mutate(deletePackagingData, { onSuccess });
  };

  const addPackageData = (packageData: any) => {
    const onSuccess = (data: any) => {
      onPackagingSave(data.packagingId);
      saveNewDrawingItem("created");
      commentResetValue("Package created successfully");
      if (packagingId) {
        refetchData(packagingId);
        return;
      }
      savePackingNodeData(data.packagingId);
    };

    create.mutate(packageData, { onSuccess });
  };

  const editPackageData = (packageData: any) => {
    const onSuccess = (data: any) => {
      onPackagingSave(data.packagingId);
      removeNewDrawingItemStatus();
      commentResetValue("Package updated successfully");
      refetchData();
    };

    update.mutate(packageData, { onSuccess });
  };

  const handlePackageValidation = () => {
    const validationError = validatePackageData({
      packagingType,
      unitSizes,
      palletLength,
      palletWidth,
      defaultMaterialType: defaultType,
      levelTitle: levelMeta?.title || "",
      fieldName
    });

    if (validationError) {
      setError(validationError.field as any, {
        type: "required",
        message: validationError.message
      });
      errorView(validationError.message);
      return false;
    }

    return true;
  };

  const packageDataInsert = (data: any, status: string) => {
    openAccordion();
    data.packageMaterial = defaultType;
    data.contentWeight = 0;
    data.totalWeightWithPackaging = 0;
    data.materialWeight = Number(data.materialWeight);
    data.materialCost = Number(data.materialCost);
    data.weightUnit = Number(materialWeightUnit);

    const packagePayload =
      gate === PACKAGING_GATE.FARM
        ? {
            data,
            packagingType,
            farmGateId: gateId,
            packagingId: status === PACKAGING_SAVE_STATUS.ADD ? packagingId : packageId
          }
        : {
            data,
            packagingType,
            processGateId: gateId,
            packagingId: status === PACKAGING_SAVE_STATUS.ADD ? packagingId : packageId
          };

    if (handlePackageValidation()) {
      if (status === PACKAGING_SAVE_STATUS.ADD) {
        addPackageData(packagePayload);
      } else {
        editPackageData(packagePayload);
      }
    }
  };

  const materialsSectionProps =
    showMaterialsSection && levelMeta
      ? {
          control: form.control,
          records: packagingData,
          packagingTypeData,
          onMaterialTypeChange: (value: number) => {
            setDefaultType(value);
            clearErrors("packageMaterial");
          },
          materialWeightUnit,
          onMaterialWeightUnitChange: setMaterialWeightUnit,
          isEditing: enableSizeEditStatus,
          editingId: packageId,
          canSave: canSavePackagingMeta,
          lockStatus: lockStatus || isLoading,
          sectionLabel: levelMeta.materialsSectionLabel,
          onAdd: handleSubmit(data => packageDataInsert(data, PACKAGING_SAVE_STATUS.ADD)),
          onUpdate: handleSubmit(data => packageDataInsert(data, PACKAGING_SAVE_STATUS.EDIT)),
          onClearDraft: clearMaterialDraft,
          onEdit: (record: any) => {
            setUnitSizeUpdate(record);
            setEnableSizeEditStatus(true);
            setPackageId(record._id);
          },
          onDelete: (record: any) => {
            setDeleteData(record);
            setVisibleDeleteModal(true);
          }
        }
      : null;

  const deleteModalProps = useMemo<PackagingDeleteModalProps>(
    () => ({
      visible: visibleDeleteModal,
      onClose: () => setVisibleDeleteModal(false),
      onCancel: () => {
        setVisibleDeleteModal(false);
        setDeleteData({});
      },
      onConfirm: () => {
        setVisibleDeleteModal(false);
        deletePackageData(deleteData);
      },
      title: "Delete package data",
      subTitle: "Are you sure you want to delete packaging data?"
    }),
    [visibleDeleteModal, deleteData]
  );

  return {
    materialsSectionProps,
    deleteModalProps
  };
}
